import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@devhop/ui";
import { useEffect, useState } from "react";

export type CancelPackageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional label for the package (e.g. template name) for description */
  packageLabel?: string;
  onConfirm: (cancelReason: string) => Promise<void>;
};

export function CancelPackageDialog({
  open,
  onOpenChange,
  packageLabel,
  onConfirm,
}: CancelPackageDialogProps) {
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCancelReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setError("ກະລຸນາປ້ອນໝາຍເຫດ");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } catch {
      setError("ຍົກເລີກແພັກເກດລົ້ມເຫຼວ");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[420px]"
        onPointerDownOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>ຍົກເລີກແພັກເກດ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            {packageLabel
              ? `ທ່ານກຳລັງຍົກເລີກແພັກເກດ: ${packageLabel}. ກະລຸນາປ້ອນໝາຍເຫດ.`
              : "ກະລຸນາປ້ອນໝາຍເຫດໃຫ້ຄົບຖ້ວນເພື່ອຢືນຢັນການຍົກເລີກ."}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">
              ໝາຍເຫດ <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="ປ້ອນໝາຍເຫດການຍົກເລີກ..."
              rows={4}
              className="resize-none"
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-destructive text-sm">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "ກຳລັງຍົກເລີກ..." : "ຍົກເລີກແພັກເກດ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
